import React from 'react'

import { Text, Button, HStack, Box } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { updatePassword } from 'aws-amplify/auth'
import { useForm } from 'react-hook-form'
import { Input } from 'src/components/__legacy__/form/input'
import { Label } from 'src/components/__legacy__/form/label'
import { Message } from 'src/components/__legacy__/message/message'
import { Modal } from 'src/components/__legacy__/modal/modal'
import { ModalContent } from 'src/components/__legacy__/modal/modal-content'
import { ModalTitle } from 'src/components/__legacy__/modal/modal-title'
import { Spacer } from 'src/components/__legacy__/spacer/spacer'
import { useUpdatePasswordChangedAt } from 'src/features/auth/api'
import { NewPasswordInput } from 'src/legacy_modules/auth/common/new-password-input'
import { useRequestState } from 'src/legacy_modules/server/use-request-state'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'
import { isDevelopmentOrStaging } from 'src/utils/env'
import * as yup from 'yup'

interface IProps {
  onClose: () => void
  closable?: boolean
  additionalMessage?: React.ReactNode
}

const validationSchema = yup.object().shape({
  prevPassword: yup.string().required(),
  password: yup.string().required(),
  passwordConfirmation: yup.string().required(),
})

type IFormValues = yup.InferType<typeof validationSchema>

export const ForgetPasswordModal: React.FC<IProps> = ({
  onClose,
  closable,
  additionalMessage,
}) => {
  const {
    requestDone,
    requestFailed,
    requestStarted,
    requesting,
    errorMessage,
  } = useRequestState()
  const toast = useMirohaToast()
  const { request: recordPasswordChange } = useUpdatePasswordChangedAt()
  const { register, handleSubmit, formState } = useForm<IFormValues>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  })
  const { isValid, touchedFields, errors } = formState

  const onSubmit = async ({
    prevPassword,
    password,
    passwordConfirmation,
  }: IFormValues) => {
    if (password !== passwordConfirmation) {
      requestFailed('パスワードが一致しません。')
      return
    }

    if (password === prevPassword) {
      requestFailed(
        '新しいパスワードは、現在のパスワードとは異なるパスワードをご入力下さい。',
      )
      return
    }

    try {
      requestStarted()
      await updatePassword({
        oldPassword: prevPassword,
        newPassword: password,
      })
      await recordPasswordChange()
      onClose()
      toast({
        status: 'success',
        title: 'パスワードを変更しました。',
      })
    } catch (error) {
      // AWS Amplify v6ではerror.nameプロパティでエラーを判定する
      // Cognito APIで定義されているエラーコードを文字列リテラルで指定
      // (ライブラリの型定義が最新のCognito APIに追従していないため)
      switch (error.name) {
        case 'InvalidParameterException':
          requestFailed('適切な形式のパスワードを入力下さい。')
          break
        case 'InvalidPasswordException':
          requestFailed(`
            半角英字（大文字、小文字をそれぞれ1文字以上ずつ）含む、
            8文字以上の半角英数字記号を入力してください。
          `)
          break
        case 'LimitExceededException':
          requestFailed(
            'アカウントがロックされています。時間を置いて再度お試し下さい。',
          )
          break
        case 'NotAuthorizedException':
          requestFailed(`
            現在のパスワードが誤っております。
            正しいパスワードをご入力下さい。
          `)
          break
        case 'PasswordHistoryPolicyViolationException':
          requestFailed(
            '過去に設定いただいているパスワードと同じ文字列はご利用できません。',
          )
          break
        default:
          requestFailed('問題が発生しました。時間を置いて再度お試し下さい。')
      }
    } finally {
      requestDone()
    }
  }

  return (
    <Modal onClose={onClose} closable={closable}>
      <ModalTitle title="パスワード変更" />

      <ModalContent>
        {additionalMessage}
        <Text>
          新しいパスワードを設定します。
          現在のパスワードと新しいパスワードを入力し、「パスワード変更」ボタンを押してください。
        </Text>

        <Spacer size={20} />

        <Label bold>現在のパスワード</Label>
        <Spacer size={10} />
        <Input type="password" width={1} {...register('prevPassword')} />
        {errors.prevPassword && touchedFields.prevPassword && (
          <div>
            <Spacer size={4} />
            <Message type="error" message="パスワードを入力してください" />
          </div>
        )}

        <Spacer size={20} />

        <NewPasswordInput
          isValid={!(errors.password && touchedFields.password)}
          register={register}
        />

        <Spacer size={20} />

        <Label bold>新しいパスワード（確認用）</Label>
        <Spacer size={10} />
        <Input
          type="password"
          placeholder="新しいパスワード（確認用）"
          width={1}
          {...register('passwordConfirmation')}
        />
        {errors.passwordConfirmation && touchedFields.passwordConfirmation && (
          <div>
            <Spacer size={4} />
            <Message type="error" message="パスワードを入力してください" />
          </div>
        )}

        {errorMessage && (
          <div>
            <Message type="error" message={errorMessage} centered />
            <Spacer size={12} />
          </div>
        )}
      </ModalContent>

      <HStack spacing={5} justify="center">
        {(closable === undefined || closable) && (
          <>
            <Button onClick={onClose} variant="outline">
              キャンセル
            </Button>
          </>
        )}
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="solid"
          disabled={!isValid || requesting}
        >
          パスワード変更
        </Button>
      </HStack>
      {isDevelopmentOrStaging() && (
        <Box justifySelf={'right'} mt={3}>
          <Button
            onClick={async () => {
              await recordPasswordChange()
              onClose()
            }}
            variant="ghost"
          >
            開発用スキップボタン
          </Button>
        </Box>
      )}
    </Modal>
  )
}
